import { Ref } from "react";

import { RequiredAsterisk } from "../common";
import { DDQForm, FormConfig } from "../types";
import FormSection, { Mode } from "./FormSection";

export type Props = {
  register?: () => Ref<any>;
  mode: Mode;
  form: DDQForm;
};

export default function TravelRulePoliciesFormSection({ register, mode, form }: Props) {
  const formConfig: FormConfig = {
    minimumRegulatoryRequirements: {
      type: "boolean",
      label: (
        <span>
          5A. Does the entity have a programme that sets minimum AML, CFT, KYC / CDD and sanctions standards per the
          requirements of the jurisdiction(s) regulatory regimes where it is license/approved/registered?
          {RequiredAsterisk}
        </span>
      ),
    },
    conductsKyc: {
      type: "boolean",
      label: (
        <span>
          5B. Does the entity conduct KYC / CDD before permitting its customers to send/receive virtual asset transfers?
          {RequiredAsterisk}
        </span>
      ),
    },
    conductsKycExplain: {
      type: "text",
      label:
        "If yes, at what threshold does the entity conduct KYC before permitting the customer to send/receive virtual asset transfers?",
    },
    entityRequiredToComply: {
      type: "boolean",
      label: (
        <span>
          5C. Is the Entity required to comply with the application of the Travel Rule standards (FATF Recommendation
          16) in the jurisdiction(s) where it is licensed / approved / registered?
          {RequiredAsterisk}
        </span>
      ),
    },
    entityRequiredToComplyExplain: {
      type: "text",
      label: "If Yes, please specify the applicable regulation(s)",
    },
    minimumThreshold: {
      type: "text",
      label: (
        <span>
          5D. What is the minimum threshold above which the entity is required to collect/send Travel Rule information?
          {RequiredAsterisk}
        </span>
      ),
    },
    useThirdParties: {
      type: "boolean",
      label: (
        <span>
          5E. Does the Entity use third parties to outsource any components of its AML, CTF & Sanctions programme (e.g.,
          KYC / CDD is outsourced to a vendor)?
          {RequiredAsterisk}
        </span>
      ),
    },
    useThirdPartiesExplain: {
      type: "text",
      label: "If Yes, please provide further details",
    },
    featuresProofOfIdentity: {
      type: "multipleChoice",
      label: (
        <span>
          5F. Does the Entity&apos;s proof of Official Identity system for individuals feature the following
          {RequiredAsterisk}
        </span>
      ),
      choices: [
        {
          label:
            "Collection: the presentation and collection of identity attributes and evidence, either in person and/or online (e.g., by filling out an online form, sending a selfie photo, uploading photos of documents such as passport national identity card or driver’s license, etc.).",
          value: "collection",
        },
        {
          label:
            "Validation: digital or physical inspection to ensure the document is authentic and its data or information is accurate (e.g., checking physical security features, expiration dates, and verifying attributes via other services).",
          value: "validation",
        },
        {
          label:
            "De-duplication: establishing that the identity attributes and evidence relate to a unique person in the ID system (e.g., via duplicate record searches, biometric recognition and/or deduplication algorithms).",
          value: "deDuplication",
        },
        {
          label:
            "Verification: linking the individual to the identity evidence provided (e.g., using biometric solutions like facial recognition and liveness detection).",
          value: "verification",
        },
        {
          label:
            "Enrollment in identity account and binding: creating the identity account and issuing and linking one or more authenticators with the identity account (e.g., passwords, one time code (OTC) generator on a smartphone, PKI19 smart cards, FIDO certificates, etc.).",
          value: "enrollmentIdentity",
        },
      ],
    },
    methodIdentification: {
      type: "choice",
      label: (
        <span>
          5G. Please indicate the method of official identity identification and verification used by the Entity?
          {RequiredAsterisk}
        </span>
      ),
      choices: [
        { label: "Manual", value: "manual" },
        { label: "Automated", value: "automated" },
        { label: "Combination of Automated and Manual", value: "manualAndAutomated" },
      ],
    },
    engagedVendors: {
      type: "text",
      label: (
        <span>
          5H. If the Entity has engaged third party vendors, please identify the vendors the Entity has engaged and what
          services they provide{RequiredAsterisk}
        </span>
      ),
    },
    identifiedCdd: {
      type: "multipleChoice",
      label: (
        <span>
          5I. When conducting CDD for Legal Entity&apos;s (Legal Persons), are each of the following identified
          {RequiredAsterisk}
        </span>
      ),
      choices: [
        { label: "Ultimate beneficial ownership", value: "ultimateBeneficial" },
        { label: "Authorised account operators / signatories (where applicable)", value: "authorisedAccountOps" },
        { label: "Key controllers", value: "keyControllers" },
        { label: "Other relevant parties", value: "other" },
        { label: "Not Applicable", value: "notApplicable" },
      ],
    },
    verifiedUbos: {
      type: "choice",
      label: <span>5J. Are Ultimate Beneficial Owners (UBOs) verified?{RequiredAsterisk}</span>,
      choices: [
        { label: "No", value: "false" },
        { label: "Yes", value: "true" },
        { label: "Not Applicable", value: "notApplicable" },
      ],
    },
    minimumThresholdBeneficialId: {
      type: "text",
      label: (
        <span>
          5K. What is the Entity&apos;s minimum (lowest) threshold percentage applied to beneficial ownership
          identification for CDD?{RequiredAsterisk}
        </span>
      ),
    },
  };

  return <FormSection title="Section 5. CDD" formConfig={formConfig} register={register} mode={mode} form={form} />;
}
